import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIncorrectAnswers } from '@/store/selectors/resultSelectors';
import Grid from '@/components/molecules/Grid';
import { AccentTextM, TextXS } from '@/pages/designSystem/Fonts';
import Card from '@/components/atoms/UI/Card/Card';
import { Article } from '@/components/templates/StyledArticle/styles';
import { sanitizeHTML } from '@/core/hooks/useSanitizedHTML';
import styled, { css } from 'styled-components';
import { basicColor } from '@/styles/template';
import Icon from '@/components/atoms/UI/Icon/Icon';
import FlexContainer from '@/components/molecules/FlexContainer';
import Counter from '@/components/atoms/UI/Counter/Counter';
import ResultAnswerImg from '@/components/features/result/ResultAnswerImg/ResultAnswerImg';
import { MathJax } from 'better-react-mathjax';
import useIsMath from '@/core/hooks/useIsMath';
import { useTranslation } from 'react-i18next';

interface IncorrectAnswersProps {}

const CorrectAnswer = styled.span`
	color: ${basicColor.secondaryDark};
  	font-weight: bold;
`;

const List = styled.div`
	
	& img {
	  max-width: 300px;
	  margin: auto;
	}
`;

const ToggleHeader = styled.div`
	cursor: pointer;
`;

const ArrowIcon = styled(Icon)<{ isOpen: boolean }>`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.3s ease;
`;

const IncorrectAnswers: React.FC<IncorrectAnswersProps> = () => {
	const [isOpen, setIsOpen] = useState(false);
	const incorrectAnswers = useSelector(selectIncorrectAnswers);
	const count = incorrectAnswers.length;

	const sanitizedAnswers = incorrectAnswers.map((question) => ({
		id: question.id,
		sanitizedQuestionLabel: sanitizeHTML(question.question.label),
		isQuestionMath: useIsMath(question.question.label),
		img: question.question.img,
		num: question.num,
		answers: question.answers.list
		.map((answer) => ({
			answerImg: answer.img,
			sanitizedAnswerLabel: sanitizeHTML(answer.label),
			isAnswerMath: useIsMath(answer.label),
			isCorrect: answer.is_correct,
			userAnswer: answer.userAnswer,
		})),
	}));

	const toggleList = () => {
		setIsOpen((prev) => !prev);
	};

	const { t } = useTranslation();
	const text_incorrect = t('result.incorrect_title' as string);

	return (
		<Card>
			<ToggleHeader onClick={toggleList}>
				<FlexContainer stretchAutoWidth gap='1'>
					<AccentTextM>{text_incorrect}</AccentTextM>
					<Counter count={count}>{count}</Counter>
					<ArrowIcon name='ArrowDown' isOpen={isOpen} />
				</FlexContainer>
			</ToggleHeader>

			{isOpen && (
				<List>
					<Article>
						<Grid col='1' gap='4'>
							{sanitizedAnswers.length > 0 && sanitizedAnswers.map((question) => {
								const QuestionText =
									<TextXS
										dangerouslySetInnerHTML={{
											__html: question.sanitizedQuestionLabel,
										}}
									/>
								return (
									<Grid col='1' gap='2' key={question.id}>
										<TextXS>
											{question.num}.{' '}
											{question.isQuestionMath ? <MathJax dynamic>{QuestionText}</MathJax> : QuestionText}
										</TextXS>

										{question.img &&
                                            <img alt='' src={question.img} />
										}

										<Grid col='1' gap='1'>
											{question.answers.map((answer, answerIndex) => {
												const ResultAnswerText =
													<>
														{(!answer.isCorrect) &&
                                                            <s dangerouslySetInnerHTML={{ __html: answer.sanitizedAnswerLabel }}/>}

														{(answer.isCorrect) &&
                                                            <CorrectAnswer
                                                                dangerouslySetInnerHTML={{ __html: answer.sanitizedAnswerLabel }}/>}
													</>


												return <div key={answerIndex}>
													{answer.isAnswerMath ?
														<MathJax dynamic>{ResultAnswerText}</MathJax> :
														ResultAnswerText
													}

													{answer.answerImg &&
                                                        <ResultAnswerImg src={answer.answerImg} correct={answer.isCorrect} />
													}
												</div>
											})}
										</Grid>

									</Grid>
								)
							})}
						</Grid>
					</Article>

				</List>
			)}
		</Card>
	);
};
export default IncorrectAnswers




